import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { addDoc, collection } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useStore } from '../store';
import type { Game, GameOutcome } from '../types';
import {
  incrementUsage,
} from '../services/usageService';
import { AlertCircle, Lock, Plus } from 'lucide-react';
import { useRipple } from '../lib/useRipple';
import { useTranslation } from '../i18n/useTranslation';

export default function NewGame() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useStore((state) => state.user);
  const profileLoading = useStore((state) => state.profileLoading);
  const getUsageInfo = useStore((state) => state.getUsageInfo);
  const setShowUpgradeModal = useStore((state) => state.setShowUpgradeModal);
  const syncProfileCounts = useStore((state) => state.syncProfileCounts);
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [opponent, setOpponent] = useState('');
  const [competitionType, setCompetitionType] =
    useState<Game['competitionType']>('regular-season');
  const [shotsFaced, setShotsFaced] = useState<string>('');
  const [saves, setSaves] = useState<string>('');
  const [outcome, setOutcome] = useState<GameOutcome>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { createRipple } = useRipple();

  // Get usage info from centralized store
  const usageInfo = getUsageInfo('game');

  // Helper to check if form should be disabled
  const isFormDisabled = loading || Boolean(usageInfo && !usageInfo.allowed);

  const handleUpgrade = async () => {
    if (!user) return;
    setShowUpgradeModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setLoading(true);
    setError('');

    // Force a fresh check by syncing profile counts before creating game
    try {
      await syncProfileCounts(user.uid);
    } catch (error) {
      console.error('Error syncing profile before game creation:', error);
    }

    // Get fresh usage info after sync
    const freshUsageInfo = getUsageInfo('game');

    // Check usage limits with fresh data
    if (freshUsageInfo && !freshUsageInfo.allowed) {
      setError(
        'You have reached your free game limit. Please upgrade to continue.'
      );
      setLoading(false);
      return;
    }

    try {
      // Parse and validate the date
      const gameDate = new Date(date);

      if (isNaN(gameDate.getTime())) {
        setError(t('game.validation.pleaseEnterValidDate'));
        setLoading(false);
        return;
      }

      // Store the date as a timestamp for Firestore
      const gameData = {
        userId: user.uid,
        date: gameDate.getTime(), // Store as timestamp
        opponent,
        competitionType,
        shotsFaced: shotsFaced ? parseInt(shotsFaced, 10) : null,
        saves: saves ? parseInt(saves, 10) : null,
        outcome,
        createdAt: Date.now(), // Store as timestamp
      };

      const docRef = await addDoc(collection(db, 'games'), gameData);

      // Increment usage count after successful creation
      if (!freshUsageInfo?.hasPaid) {
        await incrementUsage(user.uid, 'game');
      }

      navigate(`/game/${docRef.id}`);
    } catch (err) {
      console.error('Error creating game:', err);
      setError('Failed to create game');
    } finally {
      setLoading(false);
    }
  };

  // Show loading state while profile is loading
  if (profileLoading) {
    return (
      <div className='flex items-center justify-center min-h-screen px-4'>
        <div className='glass-card max-w-md w-full p-8 text-center animate-float'>
          <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4'></div>
          <p className='text-slate-600 font-medium'>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className='max-w-2xl mx-auto px-4 py-8 space-y-6'>
      {/* Header */}
      <div className='glass-card p-6'>
        <div className='flex justify-between items-start'>
          <div>
            <h1 className='text-3xl font-bold text-primary-600 mb-2 italic'>
              {t('newGame.title')}
            </h1>
            <p className='text-muted'>
              {t('newGame.subtitle')}
            </p>
          </div>
          {usageInfo && !usageInfo.hasPaid && usageInfo.usage && (
            <div className='text-sm font-medium text-slate-600 bg-slate-100/50 px-3 py-1 rounded-full'>
              Games: {usageInfo.usage.used}/{usageInfo.usage.limit}
            </div>
          )}
        </div>
      </div>

      {/* Game Limit Warning */}
      {usageInfo && !usageInfo.allowed && (
        <div className='glass-card p-6 border-amber-200/30 bg-amber-50/40'>
          <div className='flex items-start'>
            <Lock className='h-5 w-5 text-amber-600 mr-3 mt-0.5 flex-shrink-0' />
            <div className='flex-1'>
              <h3 className='text-sm font-semibold text-amber-800 mb-1'>
                {t('newGame.gameLimitReached')}
              </h3>
              <p className='text-sm text-amber-700 mb-4'>
                You've used {usageInfo.usage?.used || 0} of{' '}
                {usageInfo.usage?.limit || 0} free games. Upgrade to get
                unlimited access.
              </p>
              <button
                onClick={(e) => {
                  createRipple(e);
                  handleUpgrade();
                }}
                disabled={loading}
                className='btn-mui-primary btn-ripple'
              >
                {loading ? (
                  <div className='h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin' />
                ) : (
                  <Plus className='h-5 w-5' />
                )}
                <span>
                  {loading ? t('common.loading') : t('newGame.upgradeForMoreGames')}
                </span>
              </button>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className='glass-card p-4 border-red-200/30 bg-red-50/40 flex items-start'>
          <AlertCircle className='h-5 w-5 text-red-500 mr-3 mt-0.5 flex-shrink-0' />
          <span className='text-red-700 font-medium'>{error}</span>
        </div>
      )}

      {/* Game Form */}
      <div className='glass-card p-6'>
        <form onSubmit={handleSubmit} className='space-y-6'>
          <div>
            <label
              htmlFor='date'
              className='block text-sm font-medium text-gray-700'
            >
              {t('newGame.gameDate')}
            </label>
            <input
              type='date'
              id='date'
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className='mt-1 block w-full px-4 py-3 rounded-md border-gray-300 shadow-sm focus:border-[#1b3968] focus:ring-[#1b3968]'
              required
              disabled={isFormDisabled}
            />
            <p className='mt-1 text-sm text-gray-500'>
              {t('common.futureDatesMessage')}
            </p>
          </div>

          <div>
            <label
              htmlFor='opponent'
              className='block text-sm font-medium text-gray-700'
            >
              {t('newGame.opponent')}
            </label>
            <input
              type='text'
              id='opponent'
              value={opponent}
              onChange={(e) => setOpponent(e.target.value)}
              className='mt-1 block w-full px-4 py-3 rounded-md border-gray-300 shadow-sm focus:border-[#1b3968] focus:ring-[#1b3968]'
              placeholder={t('newGame.opponentPlaceholder')}
              required
              disabled={isFormDisabled}
            />
          </div>

          <div>
            <label
              htmlFor='competition-type'
              className='block text-sm font-medium text-gray-700'
            >
              {t('newGame.competitionType')}
            </label>
            <select
              id='competition-type'
              value={competitionType}
              onChange={(e) =>
                setCompetitionType(e.target.value as Game['competitionType'])
              }
              className='mt-1 block w-full px-4 py-3 rounded-md border-gray-300 shadow-sm focus:border-[#1b3968] focus:ring-[#1b3968]'
              disabled={isFormDisabled}
            >
              <option value='regular-season'>{t('game.competitionTypes.regular-season')}</option>
              <option value='playoff'>{t('game.competitionTypes.playoff')}</option>
              <option value='tournament'>{t('game.competitionTypes.tournament')}</option>
              <option value='exhibition'>{t('game.competitionTypes.exhibition')}</option>
              <option value='practice'>{t('game.competitionTypes.practice')}</option>
            </select>
          </div>

          <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
            <div>
              <label
                htmlFor='shots-faced'
                className='block text-sm font-medium text-gray-700'
              >
                {t('newGame.shotsFaced')}
              </label>
              <input
                type='number'
                id='shots-faced'
                value={shotsFaced}
                onChange={(e) => setShotsFaced(e.target.value)}
                className='mt-1 block w-full px-4 py-3 rounded-md border-gray-300 shadow-sm focus:border-[#1b3968] focus:ring-[#1b3968]'
                placeholder='0'
                min='0'
                disabled={isFormDisabled}
              />
            </div>

            <div>
              <label
                htmlFor='saves'
                className='block text-sm font-medium text-gray-700'
              >
                {t('newGame.saves')}
              </label>
              <input
                type='number'
                id='saves'
                value={saves}
                onChange={(e) => setSaves(e.target.value)}
                className='mt-1 block w-full px-4 py-3 rounded-md border-gray-300 shadow-sm focus:border-[#1b3968] focus:ring-[#1b3968]'
                placeholder='0'
                min='0'
                disabled={isFormDisabled}
              />
            </div>
          </div>

          <div>
            <label className='block text-sm font-medium text-gray-700 mb-3'>
              {t('newGame.gameOutcome')}
            </label>
            <div className='grid grid-cols-2 md:grid-cols-3 gap-3'>
              <button
                type='button'
                onClick={() => setOutcome('win')}
                className={`p-3 rounded-md border-2 text-sm font-medium transition-colors ${
                  outcome === 'win'
                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
                disabled={isFormDisabled}
              >
                {t('game.outcomes.win')}
              </button>
              <button
                type='button'
                onClick={() => setOutcome('loss')}
                className={`p-3 rounded-md border-2 text-sm font-medium transition-colors ${
                  outcome === 'loss'
                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
                disabled={isFormDisabled}
              >
                {t('game.outcomes.loss')}
              </button>
              <button
                type='button'
                onClick={() => setOutcome('win-ot')}
                className={`p-3 rounded-md border-2 text-sm font-medium transition-colors ${
                  outcome === 'win-ot'
                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
                disabled={isFormDisabled}
              >
                {t('game.outcomes.win-ot')}
              </button>
              <button
                type='button'
                onClick={() => setOutcome('loss-ot')}
                className={`p-3 rounded-md border-2 text-sm font-medium transition-colors ${
                  outcome === 'loss-ot'
                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
                disabled={isFormDisabled}
              >
                {t('game.outcomes.loss-ot')}
              </button>
              <button
                type='button'
                onClick={() => setOutcome('win-so')}
                className={`p-3 rounded-md border-2 text-sm font-medium transition-colors ${
                  outcome === 'win-so'
                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
                disabled={isFormDisabled}
              >
                {t('game.outcomes.win-so')}
              </button>
              <button
                type='button'
                onClick={() => setOutcome('loss-so')}
                className={`p-3 rounded-md border-2 text-sm font-medium transition-colors ${
                  outcome === 'loss-so'
                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
                disabled={isFormDisabled}
              >
                {t('game.outcomes.loss-so')}
              </button>
            </div>
            <button
              type='button'
              onClick={() => setOutcome(null)}
              className='mt-2 text-sm text-gray-500 hover:text-gray-700'
              disabled={isFormDisabled}
            >
              {t('newGame.clearSelection')}
            </button>
          </div>

          <div className='flex gap-4 pt-4'>
            <button
              type='submit'
              disabled={isFormDisabled}
              className='btn-mui-primary btn-ripple flex-1 py-3 px-6'
              onClick={createRipple}
            >
              {loading ? t('newGame.creatingGame') : t('newGame.createGame')}
            </button>
            <button
              type='button'
              onClick={(e) => {
                createRipple(e);
                navigate('/dashboard');
              }}
              className='btn-mui-secondary btn-ripple px-6 py-3'
            >
              {t('newGame.cancel')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
